import { ReactElement, useEffect } from 'react';
import { FormFieldCheckbox } from '@client/common/components/Form/FormFieldCheckbox';
import { isEmpty } from '@client/common/utils/empty/empty';
import { useTranslate } from '@client/i18n/hooks';
import { ModifyNewsletters } from '@client/myAccount/newsletters/components/ModifyNewsletters';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import { NEWSLETTERS } from 'b2b-common/core/account/api/Newsletters.types';
import { LANGUAGE_CODE } from 'b2b-common/core/i18n/consts';
import { FIELD_NAME, FormValuesType } from '../UpdateFormContainer.types';
import { Props } from './Body.types';
import './Body.scss';

export const Body = ({
  updateNewslettersRecord,
  email,
  onActiveSubscriberChange,
  onCheckboxChange,
  wereCheckboxesChanged,
}: Props): ReactElement => {
  const { handleSubmit, values, resetForm } = useFormikContext<FormValuesType>();
  const t = useTranslate();
  const isActiveSubscriber = values[FIELD_NAME.activeSubscriber];

  const newsletterNames = {
    [NEWSLETTERS.AWN]: t('Newsletter AWN - IT News DE'),
    [NEWSLETTERS.ACE]: t('Newsletter ACE - CE-News DE'),
    [NEWSLETTERS.AEN]: t('Newsletter AEN - Elektro-News DE'),
    [NEWSLETTERS.ATN]: t('Newsletter ATN - Telco-News DE'),
    [NEWSLETTERS.APN]: t('Newsletter APN - Pro-AV-News DE'),
    [NEWSLETTERS.AXN]: t('Newsletter AXN - Security-News DE'),
    [NEWSLETTERS.AWF]: t('Newsletter AWF - News IT FR'),
    [NEWSLETTERS.ACF]: t('Newsletter ACF - News CE FR'),
    [NEWSLETTERS.AEF]: t('Newsletter AEF - News electro FR'),
    [NEWSLETTERS.ATF]: t('Newsletter ATF - News telecoms FR'),
    [NEWSLETTERS.APF]: t('Newsletter APF - News AV pro FR'),
    [NEWSLETTERS.AXF]: t('Newsletter AXF - Physical security news FR'),
    [NEWSLETTERS.AAN]: t('Newsletter AAN - Academy News DE'),
    [NEWSLETTERS.AAF]: t('Newsletter AAF - Academy News FR'),
    [NEWSLETTERS.PNL]: t('Newsletter PNL - Personalized DE/FR'),
  };

  const newsletterLanguageCategories = {
    [LANGUAGE_CODE.de]: t('Shop newsletters in German'),
    [LANGUAGE_CODE.fr]: t('Shop newsletters in French'),
    [LANGUAGE_CODE.en]: t('Shop newsletters in English'),
    [LANGUAGE_CODE.it]: t('Shop newsletters in Italian'),
  };

  const { general, personalized } = values;
  const generalNewsletters = general ? Object.entries(general) : [];
  const personalizedNewsletters = personalized ? Object.entries(personalized) : [];
  const sectionHeaderClassName = classNames('MyAccount-Header', {
    'MyAccount-Header--Inactive': !isActiveSubscriber,
  });

  useEffect(
    () => {
      if (updateNewslettersRecord.hasFailed) {
        resetForm();
      }
    },
    [updateNewslettersRecord.hasFailed],
  );

  return (
    <form onSubmit={handleSubmit}>
      <div className="NewslettersDetailsPage-Box">
        <h3 className='MyAccount-Header'>{t('Global newsletter preference:')}</h3>
        <FormFieldCheckbox
          name={FIELD_NAME.activeSubscriber}
          innerComponentProps={{
            isChecked: values[FIELD_NAME.activeSubscriber],
            info: (
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    generalNewsletters.length > 0
                      ? t('Mark above field to receive newsletter according to settings below. Unmark to stop receiving newsletters entirely.')
                      : t('Mark above field to receive newsletter. Unmark to stop receiving newsletters entirely.'),
                }}
              />
            ),
            label: t('Yes, I want to receive newsletters (to {email})', { email }),
          }}
          onChange={onActiveSubscriberChange}
        />
      </div>

      {generalNewsletters.length > 0 && (
        <div className="row">
          {generalNewsletters.reduce(
            (acc, [language, newsletters]) => [
              ...acc,
              ...isEmpty(newsletters)
                ? []
                : [(
                  <div className="col-md-6 NewslettersDetailsPage-Box" key={language}>
                    <h3 className={sectionHeaderClassName}>
                      {newsletterLanguageCategories[language as LANGUAGE_CODE]}
                    </h3>
                    {Object.entries(newsletters).map(([newsletter, isActive]) => (
                      <FormFieldCheckbox
                        name={`general.${language}.${newsletter}`}
                        innerComponentProps={{
                          isChecked: values?.general?.[language as LANGUAGE_CODE]?.[newsletter as NEWSLETTERS],
                          isDisabled: !isActiveSubscriber,
                          label: newsletterNames[newsletter as NEWSLETTERS],
                        }}
                        key={newsletter}
                        onChange={onCheckboxChange}
                      />
                    ))}
                  </div>
                )],
            ],
            [] as ReactElement[],
          )}
        </div>
      )}

      {personalizedNewsletters.length > 0 && (
        <div className="NewslettersDetailsPage-Box">
          <h3 className={sectionHeaderClassName}>
            {t('Personalized')}
          </h3>
          {personalizedNewsletters.map(([newsletter, isActive]) => (
            <FormFieldCheckbox
              name={`personalized.${newsletter}`}
              innerComponentProps={{
                isChecked: values.personalized[newsletter as NEWSLETTERS],
                isDisabled: !isActiveSubscriber,
                label: newsletterNames[newsletter as NEWSLETTERS],
              }}
              key={newsletter}
              onChange={onCheckboxChange}
            />
          ))}
        </div>
      )}

      <ModifyNewsletters wereCheckboxesChanged={wereCheckboxesChanged} />
    </form>
  );
};
